import { useCallback } from 'react';

import { AmountInput } from '@/components/AmountInput';
import { ownerLeadLabel, previewAmount, won, type IncentiveAward, type IncentiveOwnerType } from '@/pages/hr/useIncentive';

/**
 * 성과급 명단 격자 — **현장대리인 화면과 본사 화면이 같은 표를 쓴다**.
 *
 * ## 왜 한 벌인가
 * 두 사람이 보는 것은 같은 명단이다. 현장은 기여도를 적고 본사는 조정액을 적는데, 그 차이는
 * **어느 칸이 열려 있느냐**뿐이라 표를 둘로 두면 칸이 하나 늘 때마다 두 곳을 고쳐야 한다.
 *
 * ## 현장안과 조정액을 나란히 둔다
 * 조정액 칸이 현장안을 덮어쓰지 않는다. 확정된 뒤에도 「현장은 얼마라고 했나」 를 답할 수 있어야
 * 하고, 그 물음이 협의의 전부다. 조정액을 비우면 현장안이 그대로 나간다 — 0 과 다르다.
 *
 * ## 금액은 서버가 낸 값이 진짜다
 * 여기서 곱하는 것은 **적는 동안 보이라고** 하는 것뿐이다. 버림까지 서버와 같게 맞춘다
 * (`SiteIncentiveService.amountOf`) — 다르면 저장 직후 숫자가 눈앞에서 바뀐다.
 */

export type AwardMode = 'SITE' | 'HQ' | 'READ';

export type AwardPatch = Partial<Pick<IncentiveAward,
  'joinedOn' | 'leftOn' | 'months' | 'contributionRate' | 'adjustedAmount' | 'note'>>;

const CELL: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'middle' };
const HEAD: React.CSSProperties = { padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' };
const NUM_INPUT: React.CSSProperties = { width: '84px', textAlign: 'right', padding: '5px 8px', fontSize: '12px' };

export function IncentiveAwardTable({
  rows,
  baseAmount,
  ownerType,
  mode,
  onChange,
}: {
  rows: IncentiveAward[];
  baseAmount: number | null;
  /** 장의 줄에 붙는 이름표를 가른다 — 현장이면 현장대리인, 부서면 팀장. */
  ownerType: IncentiveOwnerType;
  mode: AwardMode;
  onChange: (personnelId: string, patch: AwardPatch) => void;
}) {
  /*
    손잡이를 고정한다. 줄마다 인라인 화살표 함수를 만들면 한 칸을 칠 때마다 표 전체의 입력칸이
    새 함수를 받아 다시 그려지고, 스무 줄쯤에서 「입력이 안 된다」 로 체감된다.
  */
  const patch = useCallback(
    (personnelId: string, next: AwardPatch) => onChange(personnelId, next),
    [onChange],
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
            <th style={HEAD}>이름</th>
            <th style={HEAD}>직급</th>
            <th style={HEAD}>직책</th>
            <th style={HEAD}>참여 시작</th>
            <th style={HEAD}>참여 종료</th>
            <th style={{ ...HEAD, textAlign: 'right' }}>개월</th>
            <th style={{ ...HEAD, textAlign: 'right' }}>기여도(%)</th>
            <th style={{ ...HEAD, textAlign: 'right' }}>현장안</th>
            <th style={{ ...HEAD, textAlign: 'right' }}>조정액</th>
            <th style={{ ...HEAD, textAlign: 'right' }}>지급액</th>
            <th style={HEAD}>성과</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const site = previewAmount(baseAmount, row.contributionRate);
            const payable = row.adjustedAmount ?? site;
            return (
              <tr key={row.personnelId} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...CELL, fontWeight: row.ownerLead ? 700 : 500, whiteSpace: 'nowrap' }}>
                  {row.personnelName}
                </td>
                <td style={{ ...CELL, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{row.rankName ?? ''}</td>
                <td style={{ ...CELL, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {row.ownerLead ? ownerLeadLabel(ownerType, row.dutyTitle) : (row.dutyTitle ?? '')}
                </td>

                <td style={CELL}>
                  {mode === 'SITE' ? (
                    <input
                      type="date"
                      className="input"
                      style={{ width: '132px', padding: '5px 8px', fontSize: '12px' }}
                      value={row.joinedOn ?? ''}
                      onChange={(e) => patch(row.personnelId, { joinedOn: e.target.value || null })}
                    />
                  ) : (row.joinedOn ?? '')}
                </td>
                <td style={CELL}>
                  {mode === 'SITE' ? (
                    <input
                      type="date"
                      className="input"
                      style={{ width: '132px', padding: '5px 8px', fontSize: '12px' }}
                      value={row.leftOn ?? ''}
                      onChange={(e) => patch(row.personnelId, { leftOn: e.target.value || null })}
                    />
                  ) : (row.leftOn ?? '')}
                </td>

                {/*
                  개월은 **직접 적는다**. 날짜 둘로 계산해 잠그면 중간에 빠졌다 돌아온 사람이 적을
                  자리를 잃는다 — 비우면 서버가 두 날짜로 채워 준다.
                */}
                <td style={{ ...CELL, textAlign: 'right' }}>
                  {mode === 'SITE' ? (
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      className="input"
                      style={{ ...NUM_INPUT, width: '68px' }}
                      value={row.months ?? ''}
                      onChange={(e) => patch(row.personnelId, {
                        months: e.target.value === '' ? null : Number(e.target.value),
                      })}
                    />
                  ) : (row.months ?? '')}
                </td>

                <td style={{ ...CELL, textAlign: 'right' }}>
                  {mode === 'SITE' ? (
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="input"
                      style={{ ...NUM_INPUT, width: '72px' }}
                      value={row.contributionRate ?? ''}
                      onChange={(e) => patch(row.personnelId, {
                        contributionRate: e.target.value === '' ? null : Number(e.target.value),
                      })}
                    />
                  ) : (row.contributionRate ?? '')}
                </td>

                <td style={{ ...CELL, textAlign: 'right', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {won(site)}
                </td>

                <td style={{ ...CELL, textAlign: 'right' }}>
                  {mode === 'HQ' ? (
                    <AmountInput
                      className="input"
                      style={{ ...NUM_INPUT, width: '112px' }}
                      value={row.adjustedAmount == null ? '' : String(row.adjustedAmount)}
                      onChange={(next) => patch(row.personnelId, {
                        adjustedAmount: next === '' || next === '-' ? null : Number(next),
                      })}
                    />
                  ) : (won(row.adjustedAmount))}
                </td>

                <td style={{ ...CELL, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {won(payable)}
                </td>

                <td style={{ ...CELL, minWidth: '180px' }}>
                  {mode === 'SITE' ? (
                    <input
                      type="text"
                      className="input"
                      style={{ width: '100%', minWidth: '180px', padding: '5px 8px', fontSize: '12px' }}
                      value={row.note ?? ''}
                      onChange={(e) => patch(row.personnelId, { note: e.target.value || null })}
                    />
                  ) : (row.note ?? '')}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={11} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                배치된 재직자가 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
